"use client";

import { useEffect, useRef } from "react";
import {
  animate,
  motion,
  useInView,
  useMotionValue,
  useReducedMotion,
  useScroll,
  useTransform,
  type Variants,
} from "motion/react";
import { cn } from "@/lib/utils";

// Material-style "emphasized decelerate" easing, shared by every entrance.
export const ease = [0.05, 0.7, 0.1, 1] as const;

const rise: Variants = {
  hidden: { opacity: 0, y: 28 },
  show: { opacity: 1, y: 0, transition: { duration: 0.8, ease } },
};

/** Fades and rises its children into place, on load (`immediate`) or when scrolled into view. */
export function Reveal({
  children,
  className,
  delay = 0,
  immediate = false,
}: {
  children: React.ReactNode;
  className?: string;
  delay?: number;
  immediate?: boolean;
}) {
  const trigger = immediate
    ? { animate: "show" }
    : { whileInView: "show", viewport: { once: true, amount: 0.25 } };
  return (
    <motion.div
      className={className}
      variants={rise}
      initial="hidden"
      transition={{ delay }}
      {...trigger}
    >
      {children}
    </motion.div>
  );
}

/** Reveals each <StaggerItem> child one after another. */
export function Stagger({
  children,
  className,
  as = "div",
  gap = 0.12,
  immediate = false,
}: {
  children: React.ReactNode;
  className?: string;
  as?: "div" | "ol" | "ul" | "h1";
  gap?: number;
  immediate?: boolean;
}) {
  const Comp = motion[as];
  const trigger = immediate
    ? { animate: "show" }
    : { whileInView: "show", viewport: { once: true, amount: 0.15 } };
  return (
    <Comp
      className={className}
      initial="hidden"
      variants={{ hidden: {}, show: { transition: { staggerChildren: gap } } }}
      {...trigger}
    >
      {children}
    </Comp>
  );
}

export function StaggerItem({
  children,
  className,
  as = "div",
}: {
  children: React.ReactNode;
  className?: string;
  as?: "div" | "li" | "span";
}) {
  const Comp = motion[as];
  return (
    <Comp className={className} variants={rise}>
      {children}
    </Comp>
  );
}

/** Image panel that settles from a slight zoom on load, then drifts with scroll. */
export function ParallaxPanel({
  children,
  className,
  intro = false,
}: {
  children: React.ReactNode;
  className?: string;
  intro?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], reduce ? ["0%", "0%"] : ["-6%", "6%"]);

  return (
    <div ref={ref} className={cn("relative overflow-hidden", className)}>
      <motion.div
        className="absolute inset-[-8%]"
        style={{ y }}
        initial={intro ? { scale: 1.12 } : false}
        animate={intro ? { scale: 1 } : undefined}
        transition={{ duration: 1.4, ease }}
      >
        {children}
      </motion.div>
    </div>
  );
}

/** Panel that grows into place as it scrolls up the screen. */
export function GrowPanel({ children, className }: { children: React.ReactNode; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "center center"] });
  const scale = useTransform(scrollYProgress, [0, 1], reduce ? [1, 1] : [0.9, 1]);
  const radius = useTransform(scrollYProgress, [0, 1], reduce ? [28, 28] : [64, 28]);

  return (
    <motion.div ref={ref} className={className} style={{ scale, borderRadius: radius }}>
      {children}
    </motion.div>
  );
}

/** Counts up to `value` the first time it scrolls into view. Screen readers get the final number. */
export function CountUp({ value, className }: { value: number; className?: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  const inView = useInView(ref, { once: true, amount: 0.6 });
  const reduce = useReducedMotion();
  const count = useMotionValue(0);
  const text = useTransform(count, (v) => Math.round(v).toLocaleString("en-US"));

  useEffect(() => {
    if (!inView) return;
    if (reduce) {
      count.set(value);
      return;
    }
    const controls = animate(count, value, { duration: 2.2, ease });
    return () => controls.stop();
  }, [inView, reduce, value, count]);

  return (
    <span className={className}>
      <span className="sr-only">{value.toLocaleString("en-US")}</span>
      <motion.span ref={ref} aria-hidden>
        {text}
      </motion.span>
    </span>
  );
}
